/**
 * CHG-053 REQ-V53-021: 이슈 이동 다이얼로그
 */
import { useState, useEffect } from 'react';
import { X, AlertTriangle, ArrowRight, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useMovePreview, useMoveIssue } from '../../hooks/useIssueMove';
import type { MovePreviewResult } from '../../services/issueMoveService';

interface Props {
  issueId: string;  // cuid
  isOpen: boolean;
  onClose: () => void;
  onMoved?: (newIssueId: string) => void;
  projects: { id: string; name: string; projectKey: string }[];
}

export default function IssueMoveDialog({ issueId, isOpen, onClose, onMoved, projects }: Props) {
  const previewMut = useMovePreview();
  const moveMut = useMoveIssue();

  const [targetProjectId, setTargetProjectId] = useState('');
  const [preview, setPreview] = useState<MovePreviewResult | null>(null);
  const [targetTypeId, setTargetTypeId] = useState('');
  const [targetStatusId, setTargetStatusId] = useState('');
  const [keepCustomFields, setKeepCustomFields] = useState(false);

  // 프로젝트 선택 시 미리보기
  useEffect(() => {
    if (!targetProjectId) { setPreview(null); return; }
    previewMut.mutate(
      { issueId, targetProjectId },
      {
        onSuccess: (data) => {
          setPreview(data);
          setTargetTypeId(data.typeMapping.autoMappedType?.issueTypeId ?? '');
          setTargetStatusId(data.statusMapping.autoMappedStatus?.statusId ?? '');
        },
      },
    );
  }, [targetProjectId]); // eslint-disable-line

  const handleMove = async () => {
    if (!targetProjectId || !targetTypeId || !targetStatusId) return;
    try {
      const result = await moveMut.mutateAsync({
        issueId,
        data: { targetProjectId, targetIssueTypeId: targetTypeId, targetStatusId, keepCustomFields },
      });
      toast.success(`이슈가 이동되었습니다: ${result.newKey}`);
      onMoved?.(result.id);
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.error ?? '이동에 실패했습니다.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <h3 className="text-base font-semibold text-gray-900">프로젝트 이동</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="size-5" /></button>
        </div>

        {/* Body */}
        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          {/* Step 1: 프로젝트 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">대상 프로젝트</label>
            <select
              value={targetProjectId}
              onChange={(e) => setTargetProjectId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
            >
              <option value="">프로젝트를 선택하세요</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.projectKey})</option>
              ))}
            </select>
          </div>

          {/* Loading */}
          {previewMut.isPending && (
            <div className="flex items-center justify-center py-4 text-gray-400">
              <Loader2 size={20} className="animate-spin mr-2" />
              미리보기 로딩 중...
            </div>
          )}

          {/* Step 2: 매핑 */}
          {preview && (
            <>
              {/* 타입 매핑 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">타입 매핑</label>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">{preview.typeMapping.sourceType.name}</span>
                  <ArrowRight size={14} className="text-gray-400" />
                  <select
                    value={targetTypeId}
                    onChange={(e) => setTargetTypeId(e.target.value)}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
                  >
                    <option value="">선택하세요</option>
                    {preview.typeMapping.availableTypes.map((t) => (
                      <option key={t.issueTypeId} value={t.issueTypeId}>{t.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 상태 매핑 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">상태 매핑</label>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">{preview.statusMapping.autoMappedStatus?.name ?? '현재 상태'}</span>
                  <ArrowRight size={14} className="text-gray-400" />
                  <select
                    value={targetStatusId}
                    onChange={(e) => setTargetStatusId(e.target.value)}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
                  >
                    <option value="">선택하세요</option>
                    {preview.statusMapping.availableStatuses.map((s) => (
                      <option key={s.statusId} value={s.statusId}>{s.name} ({s.category})</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 경고 */}
              {(preview.fieldCompatibility.willBeLost.length > 0 || preview.hierarchyWarnings.length > 0) && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <AlertTriangle size={14} className="text-amber-600" />
                    <span className="text-sm font-medium text-amber-700">경고</span>
                  </div>
                  {preview.fieldCompatibility.willBeLost.length > 0 && (
                    <p className="text-sm text-amber-600">
                      다음 커스텀 필드 데이터가 삭제됩니다: {preview.fieldCompatibility.willBeLost.join(', ')}
                    </p>
                  )}
                  {preview.hierarchyWarnings.map((w, i) => (
                    <p key={i} className="text-sm text-amber-600">{w}</p>
                  ))}
                </div>
              )}

              {/* 옵션 */}
              {preview.fieldCompatibility.willBeLost.length > 0 && (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={keepCustomFields}
                    onChange={(e) => setKeepCustomFields(e.target.checked)}
                    className="w-4 h-4 accent-blue-600"
                  />
                  <span className="text-gray-600">호환 안 되는 커스텀 필드 데이터 유지</span>
                </label>
              )}

              {/* 요약 */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>{preview.issue.issueId}</strong>을(를) 프로젝트 <strong>{preview.targetProject.name}</strong>로 이동합니다.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">취소</button>
          <button
            onClick={handleMove}
            disabled={!targetProjectId || !targetTypeId || !targetStatusId || moveMut.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {moveMut.isPending ? '이동 중...' : '이동'}
          </button>
        </div>
      </div>
    </div>
  );
}
